import React, { useEffect, useState } from 'react';

import { Form } from '@opendoor/atoms/form';
import { Text } from '@opendoor/atoms/text';
import { globalObservability } from '@opendoor/observability/slim';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { useAnalyticsPrefix } from '../../AuthContext';
import { GENERIC_AUTH_FAILURE_MESSAGE } from '../../errors';
import { useAccountForm } from '../AccountFormContext';
import { ACCOUNT_FORM_SMS_CONSENT } from './marketingConsent';
import { NewPasswordField, PASSWORD_FIELD_NAME } from './NewPasswordField';
import { passwordSchema } from './passwordRules';
import {
  normalizePhoneNumber,
  PHONE_FIELD_NAME,
  PhoneConsentFields,
  phoneNumberSchema,
  useSmsConsentState,
} from './PhoneConsentFields';
import { AuthFormError } from './primitives/AuthFormError';
import { ConsentDisclaimer } from './primitives/ConsentDisclaimer';
import { readEmailFromQueryParam, readNamePhoneFromQueryParams } from './queryPrefill';
import { RegistrationRoutedError } from './registrationConflict';
import type { SmsConsentSpec } from './smsConsent';

/** The values this screen collects, plus the email carried in from the entry step. */
export interface RegistrationSubmission {
  email: string;
  /** The single value the register write-path's `full_name` expects. */
  fullName: string;
  phoneNumber: string;
  /**
   * The password the user chose, absent on the `passwordless` arm. Its presence
   * is what selects the credential the write-path sends: a password, or the
   * server-generated one plus a set-up email. Never synthesize it — an absent
   * value means the user was never shown the field.
   */
  password?: string;
  /**
   * The preference-driving checkbox's state — what a `marketingSmsOptOut` write
   * records. Other boxes in the spec report through `smsConsent` only.
   */
  marketingOptIn: boolean;
  /**
   * Versioned key naming the consent copy that was actually on screen. It comes
   * from the spec that rendered, so a write that records this value can never
   * name copy the user did not see.
   */
  consentLanguage: string;
  /** Every consent checkbox's final state, keyed by its spec `name`. */
  smsConsent: Readonly<Record<string, boolean>>;
}

export interface RegistrationResult {
  redirectTo?: string;
}

/**
 * Failure signal for `register`: `messages` render verbatim in the form's
 * error region, so throw one to put the server's own words in front of the
 * user. Any other thrown value renders the generic fallback message.
 */
export class RegistrationSubmissionError extends Error {
  readonly messages: readonly string[];

  constructor(messages: string[], options?: ErrorOptions) {
    super(messages[0] ?? 'Registration failed.', options);
    this.name = 'RegistrationSubmissionError';
    this.messages = messages;
  }
}

export interface RegistrationSuccessContext {
  /** Address the account was created with. */
  email: string;
  /**
   * Credential arm the step rendered: `'password'` collected one;
   * `'passwordless'` finishes the account through an emailed set-up link.
   * OAuth sign-ups never reach this module — the social buttons complete
   * through a redirect flow — so no provider arm exists here.
   */
  method: 'password' | 'passwordless';
}

export interface RegistrationModuleProps {
  /**
   * Writes the new account. Same injectable shape as `EmailModule`'s
   * `checkHumanStatus`, bound by the shell to the `useAuth` write-path; which
   * credential it sends follows `submission.password`. When omitted, the module completes via
   * `onSuccess` with just `{ email }` (the shared success-result shape) rather
   * than registering. Resolving with no result (a success that carries no
   * redirect) is valid. A throw is the failure signal — a
   * `RegistrationSubmissionError` renders its messages in the form's error
   * region; a `RegistrationRoutedError` renders nothing and reports nothing, for
   * a failure the callback already recovered from by advancing the step;
   * anything else renders the generic message.
   */
  register?: (submission: RegistrationSubmission) => Promise<RegistrationResult | undefined>;
  /**
   * Observability side channel fired once `register` reports the account
   * created, before `onSuccess` completes (and possibly navigates). Real
   * registrations only: the `register`-less completion never fires it, and
   * neither do the login modules. A throw here is isolated — reported via
   * `trackError`, never rendered in the form's error region, and never allowed
   * to stop `onSuccess` from completing the registration.
   */
  onRegistrationSuccess?: (context: RegistrationSuccessContext) => void;
  /**
   * Heading above the form. A thin naming slot, not baked-in copy — defaults to
   * the Figma "Create your account"; pass `null` to render no heading.
   */
  heading?: React.ReactNode;
  /**
   * Terms-of-service consent line below the heading. Defaults to the Figma
   * consent copy with links to Opendoor's terms and privacy policy; pass `null`
   * to render nothing or a node to override.
   */
  consent?: React.ReactNode;
  /**
   * Label on the name field. A naming slot for surfaces that collect a name on
   * someone else's behalf (reception's agent-submitted flow reads "Full name of
   * agent"); the field itself is unchanged. @default "Full name"
   */
  nameLabel?: string;
  /**
   * The SMS consent shown with the phone field: one checkbox or several, and the
   * version recorded for them. A surface that records consent under its own
   * version (reception-fe's `marketing_sms_reception_v1`) passes its own spec;
   * the submission reports that spec's `version`, so the recorded
   * `consentLanguage` always names the copy that rendered.
   * @default ACCOUNT_FORM_SMS_CONSENT
   */
  smsConsent?: SmsConsentSpec;
  /**
   * Drops the password field and finishes the account through an emailed set-up
   * link instead. This is the experiment arm, not the default: cosmos
   * production `/login` collects a password, so collecting one is parity and
   * the emailed-link path is the thing under test (GROWTH-3103).
   *
   * Matches the polarity of `REGISTER_LITE_FLAG`, whose `"on"` is the lite
   * (passwordless) arm, so there is nothing to invert. Defaults to the control,
   * so a surface that never wires the flag gets production behavior.
   *
   * **Resolve the arm before mounting.** Gate the mount on the evaluator rather
   * than feeding a pending value in: an async read is `false` on first paint, so
   * the password field renders, a password manager can fill it, and the flip to
   * `true` unmounts the field. The module clears the password when that happens
   * — the arm at submit is the arm that writes, so a password can never be sent
   * on the passwordless arm — but the user has still typed a credential into a
   * form that then discards it. `AccountFormV2` does this for you; a bare module
   * composition owns it.
   * @default false
   */
  passwordless?: boolean;
}

const DEFAULT_HEADING = 'Create your account';

const DEFAULT_NAME_LABEL = 'Full name';

const ANALYTICS_COMPONENT = 'registration';

const baseSchema = z.object({
  email: z.email(),
  fullName: z.string().trim().min(1, 'Enter your name'),
  [PHONE_FIELD_NAME]: phoneNumberSchema,
});

// Two schemas rather than one optional rule: on the control arm the password is
// required, and an `.optional()` would let an empty submit through on the very
// arm that shows the field. Both carry the same keys so either can validate the
// form's single value shape — the passwordless arm just holds no rule for a
// field it never renders.
const controlSchema = baseSchema.extend({ [PASSWORD_FIELD_NAME]: passwordSchema });
const passwordlessSchema = baseSchema.extend({ [PASSWORD_FIELD_NAME]: z.string() });

/**
 * The V2 new-user step: the email carried in from the entry step (editable, so a
 * typo is correctable here rather than a dead end), a single full-name field, a
 * password, and the shared phone + SMS-consent pairing (phone and password
 * validation gated to the submit, unlike the lifted legacy path). Usable
 * standalone under a bare `AccountFormProvider` or as the `status: 'new'` step
 * after `EmailModule`.
 *
 * On the `passwordless` arm the password field is dropped and the account is
 * finished through an emailed set-up link instead.
 *
 * An edited address is re-checked before it becomes an error: when `register`
 * reports the address as already taken, the injected callback re-runs the
 * `/humans/status` lookup for what was actually submitted and advances to the
 * sign-in step it implies (`routeRegistrationEmailConflict`), so a corrected typo
 * that lands on a real account reaches a password or magic-link screen instead of
 * dead-ending on "Email has already been taken". A rejection the re-check says is
 * not a taken address — a rejected format or domain — still renders the server's
 * own words here. Whether any of that happens is the callback's business; this
 * module only knows that a `RegistrationRoutedError` means "already handled".
 */
export const RegistrationModule: React.FC<RegistrationModuleProps> = ({
  register,
  onRegistrationSuccess,
  heading = DEFAULT_HEADING,
  consent,
  nameLabel = DEFAULT_NAME_LABEL,
  smsConsent = ACCOUNT_FORM_SMS_CONSENT,
  passwordless = false,
}) => {
  const { email, setEmail, onSuccess, onError } = useAccountForm();
  const analyticsPrefix = useAnalyticsPrefix();
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  // Latches on a completed registration. The default completion navigates away,
  // so this is what a consumer `onSuccess` that stays on the page sees: a form
  // that can't be submitted a second time.
  const [registered, setRegistered] = useState(false);
  const componentPrefix = `${analyticsPrefix}-${ANALYTICS_COMPONENT}`;
  const analyticsName = (action: string): string => `${componentPrefix}-${action}`;
  const smsConsentState = useSmsConsentState(smsConsent);

  const resolvedConsent =
    consent === undefined ? <ConsentDisclaimer analyticsPrefix={componentPrefix} /> : consent;

  const form = useForm({
    defaultValues: {
      email: email || readEmailFromQueryParam(),
      ...readNamePhoneFromQueryParams(),
      // Never prefilled: a credential does not come from a query string.
      password: '',
    },
    validators: { onChange: passwordless ? passwordlessSchema : controlSchema },
    onSubmit: async ({ value, formApi }) => {
      setSubmitErrors([]);
      // Publish the address actually being registered so the rest of the flow
      // (the set-up-link confirmation, a consumer's onSuccess) names it rather
      // than the one the entry step looked up.
      setEmail(value.email);
      const submission: RegistrationSubmission = {
        email: value.email,
        fullName: value.fullName.trim(),
        // The schema already guaranteed a valid number by submit; normalize the
        // parsed value to E.164 so the write-path never sees the raw typed text.
        phoneNumber: normalizePhoneNumber(value.phoneNumber),
        // Omitted, not empty-stringed, on the arm that shows no field: the
        // write-path branches on presence.
        ...(passwordless ? {} : { password: value.password }),
        marketingOptIn: smsConsentState.optIn,
        consentLanguage: smsConsent.version,
        smsConsent: smsConsentState.values,
      };
      if (!register) {
        onSuccess?.({ email: value.email });
        return;
      }
      let result: RegistrationResult | undefined;
      try {
        result = await register(submission);
      } catch (error) {
        // A routed conflict is a handled recovery, not a failure: `register`
        // already advanced the step, and the new screen is the feedback. The
        // shell's step graph unmounts this screen on that change, so the error
        // region would be discarded anyway — but that is a property of the graph,
        // not of this module, and a graph that keeps the screen mounted would
        // otherwise paint the generic message over a successful recovery and
        // report it to a consumer's error channel.
        if (error instanceof RegistrationRoutedError) {
          return;
        }
        // The error region is the primary failure UX; `onError` is the
        // observability side channel a shell may wire (matching
        // PasswordModule's contract), never a replacement for the feedback.
        setSubmitErrors(
          error instanceof RegistrationSubmissionError && error.messages.length > 0
            ? [...error.messages]
            : [GENERIC_AUTH_FAILURE_MESSAGE],
        );
        onError?.(error);
        return;
      }
      // The account exists now, so the credential must not outlive the submit.
      // TanStack keeps a value whose field has unmounted, so retiring the field
      // is not enough on its own — clear the value too.
      formApi.setFieldValue('password', '');
      // Registration is one-shot: retire the form rather than leave a live
      // Continue pointed at the account that just succeeded.
      setRegistered(true);
      // Outside the try: only `register` reports through the error region and
      // `onError`. A completion callback that throws is the consumer's failure,
      // not a registration failure, so it must not render as one.
      //
      // The observability callback is additionally isolated: the account exists
      // and the form is retired, so a throw here must not stop `onSuccess` from
      // completing (navigating) — that would strand the user on a disabled form.
      try {
        onRegistrationSuccess?.({
          email: value.email,
          method: passwordless ? 'passwordless' : 'password',
        });
      } catch (error) {
        reportRegistrationTrackingError(error);
      }
      onSuccess?.({ email: value.email, redirectTo: result?.redirectTo });
    },
  });

  // The entry step sets the shared email before routing here, so the field is
  // normally seeded at mount. A manual composition can publish it later, though,
  // so carry a late arrival into the field — but never over the user's own edit,
  // which is the whole point of the field being editable. `isDirty` draws that
  // line: the user changing the field (clearing it included) sets it for good,
  // while the seed leaves it alone, so a later arrival can still land.
  useEffect(() => {
    if (!email || form.getFieldMeta('email')?.isDirty) {
      return;
    }
    form.setFieldValue('email', email, { dontUpdateMeta: true });
  }, [email, form]);

  // A late-resolving evaluator can flip the arm after the field has rendered and
  // been filled. TanStack keeps a value whose field has unmounted, so without
  // this the password would sit in form state on an arm that never sends one.
  useEffect(() => {
    if (passwordless) {
      form.setFieldValue('password', '');
    }
  }, [passwordless, form]);

  return (
    <div className="flex w-full flex-col gap-4">
      {heading != null ? (
        <Text as="h1" className="text-subheader-md text-od-content-primary">
          {heading}
        </Text>
      ) : null}
      {resolvedConsent}
      {/* Outside Form.Root on purpose: Form.Input already renders field
          errors inline, so the region carries only the server-side failure —
          connected placement would duplicate every field message. */}
      <AuthFormError serverErrors={submitErrors} />
      <Form.Root
        form={form}
        noValidate
        scrollToFirstError={false}
        className="flex w-full flex-col gap-4"
      >
        <Form.Input
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="Enter your email here"
          analyticsName={analyticsName('email')}
        />
        <Form.Input
          name="fullName"
          label={nameLabel}
          autoComplete="name"
          analyticsName={analyticsName('full-name')}
        />
        {/* Gone on success rather than merely emptied: an empty field still on
            screen fails the length rule, putting a password error in front of
            someone whose registration worked. */}
        {passwordless || registered ? null : (
          <NewPasswordField analyticsName={analyticsName('password')} />
        )}
        <PhoneConsentFields consent={smsConsentState} analyticsPrefix={componentPrefix} />
        <Form.SubmitButton
          className="w-full"
          aria-label="Continue"
          disabled={registered}
          analyticsName={analyticsName('continue')}
        >
          Continue
        </Form.SubmitButton>
      </Form.Root>
    </div>
  );
};

function reportRegistrationTrackingError(error: unknown): void {
  try {
    globalObservability.trackError(
      'registration-success-callback-failed',
      error instanceof Error ? error : new Error(String(error)),
    );
  } catch {
    // The sink being unavailable is the case this exists for.
  }
}
